import React, { useContext } from 'react';
import { IconArrow } from '@components/icons/icon-arrow';
import { IconCompany } from '@components/icons/icon-company';
import { IconMap } from '@components/icons/icon-map';
import { IconTime } from '@components/icons/icon-time';
import { ImageDate } from '@components/image-date';
import { NavigationBackButton } from '@components/navigation-back-button';
import { StepperWrapper } from '@components/stepper-wrapper';
import { Tabs } from '@components/tabs';
import { ROUNDED_TAB_BLACK_VARIANT, TAB_TYPE_ROUNDED } from '@components/tabs/tabs.type';
import { DATE_FORMAT_DAY_MONTH_YEAR } from '@constants/datetime';
import { LayoutContext } from '@contexts/layout/layout-context';
import { useCurrentEvent } from '@hooks/events/use-current-event';
import { formatDateTime } from '@utils/date-time-format/format-date-time';
import { renderIconTextEventHeader } from '@utils/event-headers/event-headers';
import { fromBackend } from '@utils/map-date/from-backend';
import classNames from 'classnames';

import { EventHeaderActions } from '../event-header-actions';

import { EventHeaderProps } from './event-header.type';

export const EventHeader = ({
  isLessThanTablet,
  buttonPreviewText,
  buttonViewText,
  buttonReportText,
  lang,
  tabs,
  steps,
  currentStep,
  publishedActions,
  unpublishedActions,
  reportPath,
  backRoute,
  openPreview,
  handleChangeTab,
}: EventHeaderProps) => {
  const { event } = useCurrentEvent();
  const { isTopElementEnabled } = useContext(LayoutContext);
  const { startingTime, name, coverUrl, venue, isPublished, duplicatePath, isLiveEvent, timezone, companyClub } =
    event ?? {};
  const companyName = companyClub?.company?.name;
  const startingDateFormatted = startingTime
    ? formatDateTime({
        date: fromBackend(startingTime, timezone).toDate(),
        locale: lang,
        format: DATE_FORMAT_DAY_MONTH_YEAR,
        timezone,
      })
    : undefined;

  return (
    <header className={classNames('w-full flex bg-surface-primary pb-4 md:pb-6', { 'pt-10': !isTopElementEnabled })}>
      <div className='flex flex-col justify-between w-full max-w-7xl mx-auto'>
        <NavigationBackButton defaultRoute={backRoute} className='pl-3.5 mb-6' forceDefaultRoute={true}>
          <IconArrow className='w-5 h-5 text-primary rotate-180' />
        </NavigationBackButton>
        <div className='flex justify-between items-start mb-6 md:mb-10 gap-6 px-4'>
          <div className='flex gap-3 md:gap-4 items-start'>
            <div className='w-16 h-16 relative shrink-0'>
              <ImageDate
                date={startingTime as number}
                name={name as string}
                timezone={timezone as string}
                width={isLessThanTablet ? 96 : 128}
                height={isLessThanTablet ? 112 : 128}
                round={'full'}
                image={coverUrl as string}
                locale={lang}
                showYear={false}
              />
            </div>
            <div>
              <h1 className='text-primary text-headline-s md:text-headline-m lg:text-headline-l mb-2 sm:mb-1.5 line-clamp-2'>
                {name}
              </h1>
              <div className='flex flex-wrap gap-x-3 gap-y-1'>
                {renderIconTextEventHeader(<IconMap className='w-5 h-5' />, venue)}
                {renderIconTextEventHeader(<IconCompany className='w-4 h-4' />, companyName)}
                {renderIconTextEventHeader(<IconTime className='w-5 h-5' />, startingDateFormatted)}
              </div>
            </div>
          </div>
          <div className='flex gap-3 items-center'>
            <EventHeaderActions
              {...{
                isPublished,
                isLiveEvent,
                isLessThanTablet,
                reportPath,
                duplicatePath,
                publishedActions,
                unpublishedActions,
                openPreview,
                buttonPreviewText,
                buttonViewText,
                buttonReportText,
              }}
            />
          </div>
        </div>
        {isPublished ? (
          <Tabs
            tabs={tabs}
            activeTab={currentStep as string}
            onChange={handleChangeTab}
            type={TAB_TYPE_ROUNDED}
            roundedVariant={ROUNDED_TAB_BLACK_VARIANT}
            scrollable
            paddingClasses='pl-4 pb-0'
          />
        ) : (
          <div className='pl-4'>
            <StepperWrapper steps={steps} currentStep={currentStep as string} />
          </div>
        )}
      </div>
    </header>
  );
};
